// Single source of truth for keeping local-development noise out of Sentry.
//
// Background (triaged 2026-06-19): the issue stream had filled with
// `ReferenceError: X is not defined` / turbopack compile errors, all of
// which originated from localhost during feature work — two cases:
//   1. `next dev`                         → environment "development"
//   2. a local `next start` run against a pulled production env, which the
//      SDK still tags "vercel-production" even though every event comes
//      from localhost / *.local.
//
// `VERCEL_ENV` can't discriminate case 2 (it's present in the pulled env),
// so we filter on the one thing a pulled env can't fake: the request host
// and the reporting machine's hostname.

import type { ErrorEvent } from '@sentry/nextjs';

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[?::1\]?)(:\d+)?$/i;

/** True for hostnames that only ever appear in local development. */
export function isLocalHostname(hostname: string | undefined): boolean {
  if (!hostname) return false;
  return LOCAL_HOST.test(hostname) || hostname.endsWith('.local');
}

/**
 * Sentry `beforeSend` hook. Drops any event that originated from localhost —
 * either by request URL (client + SSR) or by the reporting machine's
 * `server_name` (e.g. `Patricks-Mac-mini.local`).
 */
export function dropLocalhostEvents(event: ErrorEvent): ErrorEvent | null {
  const url = event.request?.url;
  if (url) {
    try {
      if (isLocalHostname(new URL(url).host)) return null;
    } catch {
      // Non-absolute or malformed URL — fall through to other checks.
    }
  }

  if (isLocalHostname(event.server_name)) return null;

  return event;
}
